import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { REHYDRATE } from 'redux-persist';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import { axiosBaseQuery } from './baseQuery';
import { getLocalStorage, LOCAL_STORAGE_NAMES } from '@libs/storage/src/lib/local';

export const productMasterApi = createApi({
  reducerPath: 'productMaster.api',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.NX_API_URL,
    prepareHeaders: (headers) => {
      const token = getLocalStorage(LOCAL_STORAGE_NAMES.TOKEN);

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      return headers;
    },
  }),
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === REHYDRATE) {
      return action.payload?.[reducerPath];
    }
  },
  tagTypes: [
    'productMaster',
    'productSale',
    'productOrder',
    'productGuide',
    'productCombination',
    'productEdit',
    'productNotice',
    'productInformationPopup',
  ],
  endpoints: (builder) => ({
    // ? BASIC
    getProductMasterList: builder.query({
      query: (data) => ({
        url: `/api/products`,
        params: data,
        method: 'GET',
      }),
      transformResponse: (response, meta: any) => {
        return {
          rows: response,
          totalCount: Number(meta?.response?.headers.get('X-Total-Count')),
        };
      },
      forceRefetch(params) {
        return true;
      },
    }),
    getProductMasterById: builder.query({
      query: (id) => ({ url: `/api/products/${id}` }),
      providesTags: ['productMaster'],
      forceRefetch(params) {
        return true;
      },
    }),
    addProductMaster: builder.mutation({
      query: (body) => ({
        url: `/api/products`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['productMaster'],
    }),
    updateProductMaster: builder.mutation({
      query: (data) => ({
        url: `/api/products/${data.id}`,
        body: data,
        method: 'PUT',
      }),
      invalidatesTags: ['productMaster'],
    }),

    // ? SALE
    updateProductSale: builder.mutation({
      query: ({ productId, ...body }) => ({
        url: `/api/products/${productId}/sale`,
        body,
        method: 'PUT',
      }),
      invalidatesTags: ['productSale'],
    }),
    getProductSale: builder.query({
      query: (id) => ({ url: `/api/products/${id}/sale` }),
      providesTags: ['productSale'],
    }),
    reflectChannelProduct: builder.mutation({
      query: ({ productId, useChannelStatus, ...body }) => {
        return {
          url: `/api/product/${productId}/supply?useChannelStatus=${useChannelStatus}`,
          body,
          method: 'POST',
        };
      },
      invalidatesTags: ['productSale'],
    }),
    addProductSale: builder.mutation({
      query: ({ productId, ...body }) => ({
        url: `/api/products/${productId}/sale`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['productSale'],
    }),
    discontinueProductSale: builder.mutation({
      query: ({ productId, ...body }) => ({
        url: `/api/products/${productId}/discontinuance`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['productSale'],
    }),

    //ORDER
    updateProductOrder: builder.mutation({
      query: (body) => ({
        url: `/api/products/${body.productId}/order`,
        body: cloneDeep(body),
        method: 'PUT',
      }),
      invalidatesTags: ['productOrder'],
    }),
    getProductOrder: builder.query({
      query: (id) => ({ url: `/api/products/${id}/order` }),
      providesTags: ['productOrder'],
    }),
    addProductOrder: builder.mutation({
      query: (body) => ({
        url: `/api/products/${body.productId}/order`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['productOrder'],
    }),
    // 마스터 상품 생산일 갱신 (P3 -> POD)
    syncProductionDays: builder.mutation({
      query: (id) => ({
        url: `/api/products/${id}/production-days`,
        method: 'POST',
      }),
    }),

    //EDIT
    updateProductEdit: builder.mutation({
      query: (body) => ({
        url: `/api/products/${body.productId}/edit`,
        body: cloneDeep(body),
        method: 'PUT',
      }),
      invalidatesTags: ['productEdit'],
    }),
    getProductEdit: builder.query({
      query: (id) => ({ url: `/api/products/${id}/edit` }),
      providesTags: ['productEdit'],
    }),
    addProductEdit: builder.mutation({
      query: (body) => ({
        url: `/api/products/${body.productId}/edit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['productEdit'],
    }),

    //GUIDE
    addProductGuide: builder.mutation({
      query: (body) => ({
        url: `/api/products/${body.productId}/guide`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['productGuide'],
    }),
    getProductGuide: builder.query({
      query: (id) => ({ url: `/api/products/${id}/guide` }),
      providesTags: ['productGuide'],
    }),
    // PDF 조건 드롭다운 값 — 레이어 이름은 PDF 파일서버가 정확일치를 요구해 BE 가 정본이다
    getProductGuidePdfEnums: builder.query({
      query: () => ({ url: `/api/products/guide/pdf-enums` }),
    }),
    updateProductGuide: builder.mutation({
      query: (body) => ({
        url: `/api/products/${body.productId}/guide`,
        body: cloneDeep(body),
        method: 'PUT',
      }),
      invalidatesTags: ['productGuide'],
    }),
    //NOTICE
    addProductNotice: builder.mutation({
      query: (body) => ({
        url: `/api/products/${body.productId}/notice`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['productNotice'],
    }),
    getProductNotice: builder.query({
      query: (id) => ({ url: `/api/products/${id}/notice` }),
      providesTags: ['productNotice'],
    }),
    updateProductNotice: builder.mutation({
      query: (body) => ({
        url: `/api/products/${body.productId}/notice`,
        body: cloneDeep(body),
        method: 'PUT',
      }),
      invalidatesTags: ['productNotice'],
    }),

    // COMBINATION
    addProductCombination: builder.mutation({
      query: (body) => ({
        url: `/api/products/${body.productId}/combination`,
        method: 'POST',
        body: cloneDeep(body.combinationProduct),
      }),
      invalidatesTags: ['productCombination'],
    }),
    getProductCombination: builder.query({
      query: (id) => ({ url: `/api/products/${id}/combination` }),
      providesTags: ['productCombination'],
    }),
    updateProductCombination: builder.mutation({
      query: (body) => ({
        url: `/api/products/${body.productId}/combination`,
        body: cloneDeep(body.combinationProduct),
        method: 'PUT',
      }),
      invalidatesTags: ['productCombination'],
    }),

    // For channel product information popup
    getMasterProductGuide: builder.query({
      query: (id) => ({ url: `/api/master-products/${id}/guide` }),
      providesTags: ['productInformationPopup'],
    }),
    getMasterProductShipping: builder.query({
      query: (id) => ({ url: `/api/master-products/${id}/shipping` }),
      providesTags: ['productInformationPopup'],
    }),
    getMasterProductPrice: builder.query({
      query: (id) => ({ url: `/api/master-products/${id}/price` }),
      providesTags: ['productInformationPopup'],
    }),
    getMasterProductNotice: builder.query({
      query: (id) => ({ url: `/api/master-products/${id}/notice` }),
      providesTags: ['productInformationPopup'],
    }),
    getMasterProductVariantPrice: builder.query({
      query: ({ id, poIds, channelProductId }) => {
        const masterProductQueryParams = map(poIds, (id) => {
          return `poIds=${id}`;
        }).join('&');
        const channelProductQueryParam = channelProductId ? `&channelProductId=${channelProductId}` : '';

        return {
          url: `/api/master-products/${id}/variants/price?${masterProductQueryParams}${channelProductQueryParam}`,
        };
      },
    }),
    getProductRevisions: builder.query({
      query: ({ poIds }) => ({ url: `/api/products/${poIds}/revisions` }),
    }),

    // DELIVERY COMPANY
    getDeliveryCompanyList: builder.query({
      query: () => ({ url: `/api/delivery-company` }),
    }),
    addDeliveryCompany: builder.mutation({
      query: (body) => ({
        url: `/api/delivery-company`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['productMaster'],
    }),

    // APPAREL CATEGORY EASE (여유분) - 사이즈표 apparelEaseId 선택용
    getCategoryEaseList: builder.query({
      query: () => ({ url: `/api/apparel-size-meta/category-ease` }),
    }),
  }),
});

export const {
  useGetProductMasterListQuery,
  useGetProductMasterByIdQuery,
  useAddProductMasterMutation,
  useUpdateProductMasterMutation,

  useGetProductSaleQuery,
  useAddProductSaleMutation,
  useUpdateProductSaleMutation,
  useDiscontinueProductSaleMutation,
  useReflectChannelProductMutation,

  useAddProductOrderMutation,
  useGetProductOrderQuery,
  useUpdateProductOrderMutation,
  useSyncProductionDaysMutation,

  useAddProductEditMutation,
  useGetProductEditQuery,
  useUpdateProductEditMutation,

  useAddProductGuideMutation,
  useGetProductGuideQuery,
  useGetProductGuidePdfEnumsQuery,
  useUpdateProductGuideMutation,

  useAddProductNoticeMutation,
  useGetProductNoticeQuery,
  useUpdateProductNoticeMutation,

  useAddProductCombinationMutation,
  useGetProductCombinationQuery,
  useUpdateProductCombinationMutation,

  useLazyGetMasterProductGuideQuery,
  useLazyGetMasterProductShippingQuery,
  useLazyGetMasterProductPriceQuery,
  useLazyGetMasterProductNoticeQuery,
  useLazyGetMasterProductVariantPriceQuery,

  useLazyGetProductRevisionsQuery,

  useGetDeliveryCompanyListQuery,
  useAddDeliveryCompanyMutation,

  useGetCategoryEaseListQuery,
} = productMasterApi;
